import type { CoreTransactionRow } from '@/types';

/** Columns of the partner feed, in the order game-core returns them. */
const CORE_COLUMNS: (keyof CoreTransactionRow)[] = [
  'id',
  'dt',
  'action',
  'type',
  'game_uuid',
  'bet_id',
  'round_id',
  'player_id',
  'amount',
  'currency',
  'rollbacked',
  'rollback_id',
  'client_trx_id',
  'freeround_id',
  'session_id',
  'play_service_id',
];

/**
 * The partner feed exactly as received — this is the operator's deliverable,
 * not our rendering of it. When a partner disputes our reconciliation data,
 * this file is the thing under discussion, so it must be reproducible: same
 * columns, same order, same values they got.
 *
 * ⚠️ `amount` arrives as ClickHouse **Float64**, so the feed has already lost
 * the decimal precision MySQL holds (1.40 reaches the partner as `1.4`, and a
 * value with more than ~15 significant digits is approximated). Exporting it
 * verbatim is the honest choice — reformatting here would show the partner
 * something they never received. Quote exact amounts from our own record.
 */
export function coreToCsv(rows: CoreTransactionRow[]): string {
  const header = CORE_COLUMNS.join(',');
  const lines = rows.map((row) =>
    CORE_COLUMNS.map((col) => escapeCell(stringify(row[col]))).join(','),
  );
  return [header, ...lines].join('\n');
}

function stringify(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

/** RFC-4180 quoting: wrap when the value could break the row, double inner quotes. */
function escapeCell(value: string): string {
  const v = value ?? '';
  return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

export function downloadCsv(filename: string, csv: string): void {
  // BOM so Excel opens UTF-8 correctly.
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Ticket-friendly filename from the CLIENT NAME, not the play_service_id — a
 * 40-char opaque hash tells nobody which project the export is for. Sanitised
 * so a name with spaces or punctuation cannot inject a path.
 */
export function reconFilename(kind: 'partner-feed', client: string, from: string): string {
  const safe = (s: string) => s.replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 40);
  return `reconciliation-${kind}_${safe(client)}_${safe(from.slice(0, 10))}.csv`;
}
